import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import {
  Heart,
  Target,
  Users,
  Microscope,
  HandshakeIcon,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  Check,
  TrendingUp,
  Award,
  Building2,
  Stethoscope
} from 'lucide-react';
import { mockData } from '../mock';
import { DonationDialog } from '../components/DonationDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const { toast } = useToast();
  const [selectedDonationType, setSelectedDonationType] = useState('Donor');
  const [donationOpen, setDonationOpen] = useState(false);
  const [donationAmount, setDonationAmount] = useState(null);
  const [campaignRaised, setCampaignRaised] = useState(null);

  useEffect(() => {
    axios.get(`${API}/campaign/progress`)
      .then(({ data }) => setCampaignRaised(data.raised))
      .catch(() => setCampaignRaised(0));
  }, []);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
    type: 'general'
  });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast({
        title: "Message Sent!",
        description: "Thank you for reaching out. We'll get back to you soon.",
      });
      setContactForm({ name: '', email: '', message: '', type: 'general' });
    } catch (err) {
      toast({
        title: "Could not send message",
        description: err.response?.data?.detail || "Please try again shortly.",
        variant: "destructive",
      });
    }
  };

  const handleDonateClick = (amount) => {
    setDonationAmount(amount);
    setDonationOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_4f329244-455b-4bf6-8c8b-a4ec61e13f95/artifacts/dly2iwzc_NPC%20Logo.png" 
              alt="Foundation Logo" 
              className="h-12 w-12 object-contain"
            />
            <div>
              <h1 className="text-lg font-bold text-gray-900">The Genevieve Fourie Teles Foundation</h1>
              <p className="text-xs text-gray-600">For Combating Diseases</p>
              <p className="text-xs text-gray-500 font-medium">NPC: 2025/918417/08</p>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#about" className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">About</a>
            <a href="#campaigns" className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">Campaigns</a>
            <a href="#support" className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">Support</a>
            <a href="/pledge" className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">Pledge</a>
            <a href="#contact" className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">Contact</a>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => handleDonateClick(null)} data-testid="header-donate-button">
              <Heart className="w-4 h-4 mr-2" />
              Donate Now
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200">
                <Microscope className="w-3 h-3 mr-1" />
                Advancing Chronic Pain Research
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Transforming Lives Through Research
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Funding critical research into chronic pain diseases to secure better medical coverage and improve patient outcomes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => handleDonateClick(null)}>
                  <Heart className="w-5 h-5 mr-2" />
                  Make a Donation
                </Button>
                <Button size="lg" variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50" onClick={() => window.location.href = '/pledge'}>
                  Pledge Your Support
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
            <div className="flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_4f329244-455b-4bf6-8c8b-a4ec61e13f95/artifacts/nw7xghph_Slogan%20200x200.png" 
                alt="Yaweh: Transforming Lives, One Purpose at a Time" 
                className="w-full max-w-md object-contain rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">About The Foundation</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              The Genevieve Fourie Teles Foundation is a passion project dedicated to making a real difference 
              in the lives of those suffering from chronic pain diseases. We believe that no one should be denied 
              proper medical coverage due to lack of research or documentation.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Our mission is to source donor funding that drives comprehensive research into chronic pain conditions. 
              The research outputs we support are used to establish evidence-based treatment protocols and secure 
              better medical insurance coverage for patients who desperately need it.
            </p>
          </div>
        </div>
      </section>

      {/* My Story Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">My Story</h2>
              <p className="text-lg text-gray-600">The Journey Behind the Foundation</p>
            </div>
            
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8 md:p-12">
                <div className="space-y-6 text-gray-700 leading-relaxed">
                  <p className="text-lg">
                    This foundation was born from a deeply personal journey—one that began with witnessing 
                    the profound struggles of those living with chronic pain diseases, and the heartbreaking 
                    reality of medical systems that often fail to provide adequate support.
                  </p>
                  
                  <p>
                    I haven't just witnessed this struggle from the sidelines—I've lived it. My own journey began 
                    with six long years of searching for answers before finally being diagnosed with stage 4 
                    endometriosis in 2014. Those years were filled with dismissals, misdiagnoses, and the constant 
                    question of whether my pain was "real enough" to warrant investigation.
                  </p>
                  
                  <p>
                    Since my diagnosis, I've undergone 12 laparoscopic surgeries to manage the disease, each one 
                    a reminder of how complex and relentless chronic pain conditions can be. In 2023, I finally 
                    had a hysterectomy. But my journey didn't end there—I was recently diagnosed with dermatomyositis, 
                    fibromyalgia, and lupus. Each diagnosis came with its own battles for recognition, proper treatment, 
                    and medical coverage.
                  </p>
                  
                  <p>
                    Through this deeply personal experience, I've seen firsthand how chronic pain can transform lives, 
                    not just physically, but emotionally and financially. I've fought battles on multiple fronts—against 
                    my own body, against insurance companies that deny coverage, and against healthcare systems that 
                    don't fully recognize or understand these conditions.
                  </p>
                  
                  <p>
                    The breaking point—or rather, the turning point—came when I realized that much of this suffering 
                    stems from a critical gap: the lack of comprehensive research and evidence-based documentation. 
                    Without solid research, chronic pain diseases remain invisible to policy makers. Without proper 
                    documentation, patients like me are denied the medical coverage we desperately need.
                  </p>
                  
                  <div className="bg-blue-50 border-l-4 border-blue-600 p-6 my-8">
                    <p className="text-blue-900 italic font-medium">
                      "I decided that if the system wouldn't change on its own, I would dedicate myself to 
                      creating that change. Every research project we fund is a step toward a world where no 
                      one is denied proper care because their pain isn't 'documented enough.'"
                    </p>
                  </div>
                  
                  <p>
                    This foundation is my commitment to transforming lives through action. It's about funding 
                    the research that medical institutions need, building the evidence that insurance companies 
                    require, and creating the documentation that changes policies. Most importantly, it's about 
                    giving hope to those who have been told their pain doesn't qualify for coverage.
                  </p>
                  
                  <p className="text-lg font-medium text-gray-900">
                    Every donation, every research project, every policy change—it all adds up to lives transformed, 
                    one purpose at a time. That's the legacy I'm building, and I invite you to be part of it.
                  </p>
                  
                  <div className="pt-6 text-right">
                    <p className="text-gray-900 font-semibold text-lg">With gratitude,</p>
                    <p className="text-gray-700 text-xl font-bold mt-2">Genevieve Fourie Teles</p>
                    <p className="text-gray-600 text-sm">Founder</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tribute Section */}
            <Card className="mt-8 border-0 shadow-xl bg-gradient-to-br from-sky-50 to-blue-50">
              <CardContent className="p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div className="order-2 md:order-1">
                    <div className="space-y-4">
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 text-center md:text-left">
                        In Loving Memory of<br />Nana Margaret Mcgivern
                      </h3>
                      <div className="text-gray-700 leading-relaxed space-y-4">
                        <p className="italic">
                          Your courage, strength and the grace you portrayed although suffering most of your life with 
                          chronic pain diseases was truly inspirational to witness. I know you are watching over me and 
                          would be cheering me on. I hope to do you proud and make a difference! One patient at a time!
                        </p>
                        <p className="font-medium text-gray-900">Love you Nana</p>
                        <p className="text-gray-600">1945 - 2025</p>
                        <div className="pt-4 border-t border-blue-200">
                          <p className="text-gray-900 font-semibold">Genevieve Fourie Teles</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="order-1 md:order-2">
                    <div className="relative">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_disease-fighters/artifacts/5w2k52am_Copilot_20251113_174038.png" 
                        alt="In loving memory of Nana Margaret Mcgivern" 
                        className="w-full rounded-lg shadow-lg"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle className="text-2xl">Our Mission</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-600">Fund groundbreaking research into chronic pain diseases</p>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-600">Establish evidence-based treatment protocols</p>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-600">Secure better medical coverage for patients</p>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-600">Advocate for policy changes in healthcare systems</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle className="text-2xl">Our Vision</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-600">
                  A world where chronic pain patients receive the comprehensive medical coverage they deserve, 
                  backed by solid research and evidence-based care standards.
                </p>
                <p className="text-gray-600">
                  We envision healthcare systems that recognize and adequately support individuals living with 
                  chronic pain conditions, ensuring they have access to proper treatment without financial burden.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Current Campaigns */}
      <section id="campaigns" className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Current Campaigns</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Support our active research initiatives making a real impact in chronic pain treatment and medical coverage.
            </p>
          </div>

          {/* Featured Campaign - Endometriosis Awareness */}
          {mockData.campaigns.filter(c => c.featured).map((campaign) => {
            const raised = campaignRaised != null ? campaignRaised : campaign.currentAmount;
            const progress = Math.min(100, Math.round((raised / campaign.goalAmount) * 100));
            return (
              <Card key={campaign.id} className="mb-12 border-2 border-purple-500 shadow-2xl bg-gradient-to-br from-purple-50 to-pink-50">
                <div className="grid md:grid-cols-2 gap-6 p-6">
                  <div className="flex items-center justify-center overflow-hidden rounded-lg bg-white p-6">
                    <img 
                      src={campaign.image} 
                      alt={campaign.title} 
                      className="w-full h-auto object-contain max-h-96"
                    />
                  </div>
                  <div className="flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <Badge className="bg-purple-600 text-white hover:bg-purple-700">Featured Campaign</Badge>
                        <Badge className="bg-pink-100 text-pink-700">{campaign.category}</Badge>
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">{campaign.title}</h3>
                      <p className="text-gray-700 leading-relaxed mb-4">{campaign.description}</p>
                      
                      <div className="bg-white rounded-lg p-4 mb-4 border border-purple-200">
                        <h4 className="font-semibold text-gray-900 mb-2">How You Can Help:</h4>
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li className="flex items-start gap-2">
                            <Stethoscope className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <span><strong>Specialist Gynaecologists:</strong> Volunteer to see 10+ pro bono patients in March 2027</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Users className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <span><strong>Surgical Support:</strong> Volunteer for pro bono surgical procedures</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Heart className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <span><strong>Donors:</strong> Cover hospital, theatre, medicine, equipment, and staff costs</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700 font-medium">Campaign Progress</span>
                          <span className="font-bold text-purple-700">{progress}%</span>
                        </div>
                        <Progress value={progress} className="h-3" />
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-gray-900 text-lg" data-testid="campaign-raised-amount">R{raised.toLocaleString()}</span>
                          <span className="text-gray-600">Goal: R{campaign.goalAmount.toLocaleString()}</span>
                        </div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button className="flex-1 bg-purple-600 hover:bg-purple-700 text-white" onClick={() => handleDonateClick(5000)}>
                          <Heart className="w-4 h-4 mr-2" />
                          Donate Now
                        </Button>
                        <Button variant="outline" className="flex-1 border-purple-600 text-purple-600 hover:bg-purple-50" onClick={() => window.location.href = '/pledge'}>
                          <Stethoscope className="w-4 h-4 mr-2" />
                          Volunteer as Doctor
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* How to Support */}
      <section id="support" className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How to Support Us</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the support level that works for you and help us drive meaningful change.
            </p>
          </div>

          <div className="flex justify-center gap-4 mb-8">
            {mockData.donationTiers.map((tier) => (
              <Button
                key={tier.type}
                variant={selectedDonationType === tier.type ? 'default' : 'outline'}
                onClick={() => setSelectedDonationType(tier.type)}
                className={selectedDonationType === tier.type ? 'bg-blue-600' : ''}
              >
                {tier.type}
              </Button>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {mockData.donationTiers.map((tier) => (
              <Card key={tier.type} className={`border-2 transition-all ${
                selectedDonationType === tier.type 
                  ? 'border-blue-500 shadow-xl scale-105' 
                  : 'border-gray-200 hover:shadow-lg'
              }`}>
                <CardHeader>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    {tier.type === 'Donor' && <Heart className="w-6 h-6 text-blue-600" />}
                    {tier.type === 'Sponsor' && <Award className="w-6 h-6 text-blue-600" />}
                    {tier.type === 'Partner' && <Building2 className="w-6 h-6 text-blue-600" />}
                  </div>
                  <CardTitle className="text-2xl">{tier.title}</CardTitle>
                  <CardDescription className="text-gray-600">{tier.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-700">Benefits:</p>
                    {tier.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{benefit}</span>
                      </div>
                    ))}
                  </div>

                  <Separator />

                  {tier.customContact ? (
                    <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => document.querySelector('#contact').scrollIntoView({behavior: 'smooth'})}>
                      Contact Us for Partnership
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-gray-700">Suggested Amounts:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {tier.suggestedAmounts.map((amount) => (
                          <Button
                            key={amount}
                            variant="outline"
                            className="hover:bg-blue-50 hover:border-blue-500"
                            onClick={() => handleDonateClick(amount)}
                          >
                            R{amount.toLocaleString()}
                          </Button>
                        ))}
                      </div>
                      <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => handleDonateClick(null)}>
                        Custom Amount
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Opportunities */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Partnership Opportunities</h2>
              <p className="text-lg text-gray-600">
                Join us as a strategic partner to drive systemic change in chronic pain medical coverage.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                    <HandshakeIcon className="w-6 h-6 text-purple-600" />
                  </div>
                  <CardTitle>Corporate Partnerships</CardTitle>
                  <CardDescription>
                    Partner with us to demonstrate your commitment to healthcare advocacy and employee wellness.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-sm text-gray-600">Co-branded research initiatives</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-sm text-gray-600">Employee matching programs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-sm text-gray-600">Joint advocacy campaigns</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-teal-600" />
                  </div>
                  <CardTitle>Medical Institution Partnerships</CardTitle>
                  <CardDescription>
                    Collaborate on research projects and clinical trials to advance chronic pain treatment.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-sm text-gray-600">Research funding opportunities</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-sm text-gray-600">Data sharing agreements</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-sm text-gray-600">Publication collaboration</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Get In Touch</h2>
              <p className="text-lg text-gray-600">
                Have questions or want to learn more? We'd love to hear from you.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              <div className="space-y-6">
                <Card className="border-0 shadow-md">
                  <CardContent className="pt-6 space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Email</p>
                        <p className="text-gray-600">GFT.Yaweh@gmail.com</p>
                      </div>
                    </div>
                    <Separator />
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Location</p>
                        <p className="text-gray-600">South Africa</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle>Send Us a Message</CardTitle>
                  <CardDescription>We'll get back to you within 24-48 hours</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div>
                      <Input
                        placeholder="Your Name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Input
                        type="email"
                        placeholder="Your Email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Textarea
                        placeholder="Your Message"
                        value={contactForm.message}
                        onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                        rows={5}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="contact-submit-button">
                      Send Message
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Pledge CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold">
              Ready to Make Your Commitment?
            </h2>
            <p className="text-xl text-blue-100 leading-relaxed">
              Whether you're a medical professional, volunteer, or donor—your pledge helps us plan and 
              execute life-changing research initiatives.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-gray-100"
                onClick={() => window.location.href = '/pledge'}
              >
                <HandshakeIcon className="w-5 h-5 mr-2" />
                Make Your Pledge
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10"
                onClick={() => document.querySelector('#campaigns').scrollIntoView({behavior: 'smooth'})}
              >
                View Campaigns
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_4f329244-455b-4bf6-8c8b-a4ec61e13f95/artifacts/dly2iwzc_NPC%20Logo.png" 
                  alt="Foundation Logo" 
                  className="h-10 w-10 object-contain"
                />
                <div>
                  <span className="font-bold text-white block">GFT Foundation</span>
                  <span className="text-xs text-gray-400">NPC: 2025/918417/08</span>
                </div>
              </div>
              <p className="text-sm text-gray-400">
                Transforming Lives, One Purpose at a Time
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#about" className="hover:text-blue-400 transition-colors">About Us</a></li>
                <li><a href="#campaigns" className="hover:text-blue-400 transition-colors">Campaigns</a></li>
                <li><a href="#support" className="hover:text-blue-400 transition-colors">Support</a></li>
                <li><a href="#contact" className="hover:text-blue-400 transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Get Involved</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="/pledge" className="hover:text-blue-400 transition-colors">Make a Pledge</a></li>
                <li><a href="#support" className="hover:text-blue-400 transition-colors">Donate</a></li>
                <li><a href="#support" className="hover:text-blue-400 transition-colors">Become a Sponsor</a></li>
                <li><a href="#support" className="hover:text-blue-400 transition-colors">Partner With Us</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-blue-400 transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-blue-400 transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-blue-400 transition-colors">Tax Information</a></li>
              </ul>
            </div>
          </div>
          <Separator className="bg-gray-800 mb-8" />
          <div className="text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} The Genevieve Fourie Teles Foundation for Combating Diseases. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <DonationDialog open={donationOpen} onOpenChange={setDonationOpen} initialAmount={donationAmount} />
    </div>
  );
};

export default Home;
